import { useCallback } from 'react';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import { getInitialFiltersStateForUIMode } from '../utils/filters';
import { useCurrentView, useCurrentViewFilters } from '../internal/ViewTabs/hooks/useViewProps';
import { useHasViews } from '../internal/ViewTabs/hooks/useViewProps';
import useUIMode from '../internal/hooks/useUIMode';
import { useFiltersStateStore } from '../internal/providers/CrmObjectTypeListingClientProvider';
import { useCrmObjectType } from '../internal/providers/CrmObjectTypeListingClientProvider';
import useUpdateFilters from './useUpdateFilters';

/**
 * Returns a callback that resets filters to their default state.
 *
 * Reset behaviour:
 *  - if Views are not enabled, resets to initialFilters
 *  - if Views are enabled and there is a currently active View, resets to that View's saved filters
 *  - if there is no currently active View (eg in FOLDERS mode), resets to initialFilters
 */
export default function useResetFilters() {
  const uiModeProps = useUIMode();
  const {
    initialFilter
  } = useCrmObjectType();
  const onSetFilters = useFiltersStateStore(state => state.setFilters);
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const currentViewFilters = useCurrentViewFilters();
  const {
    onUpdateFilters
  } = useUpdateFilters();
  return useCallback(({
    newUIMode
  } = {}) => {
    const isGoingToFoldersMode = Boolean(newUIMode && newUIMode !== uiModeProps.currentUIMode && newUIMode === UI_MODE.FOLDERS);

    /**
     * This condition needs to come **before** checking for views for apps that have views + folders.
     * When moving from views to folders both @hasViews and @currentView are still defined, but since
     * we're in folder mode, we need to ignore both.
     *
     * We set the complete filter state (including sort, pagination, query) to match what
     * useCrmObjectTypeListingFilterState's useEffect would set when currentUIMode changes. This
     * ensures a single search request instead of two, since the useEffect will see isEqual(prevFilter, newFilter)
     * and skip the update.
     */
    if (isGoingToFoldersMode) {
      onSetFilters(getInitialFiltersStateForUIMode({
        initialFilter,
        uiMode: UI_MODE.FOLDERS
      }));
      return;
    }
    const initialFilterGroups = initialFilter && initialFilter.filterGroups ? initialFilter.filterGroups : [];
    const initialQuickFilters = initialFilter && initialFilter.quickFilters ? initialFilter.quickFilters : [];
    if (hasViews && currentView) {
      onUpdateFilters({
        filterGroups: currentViewFilters.filterGroups,
        quickFilters: currentViewFilters.quickFilters,
        query: ''
      });
    } else {
      onUpdateFilters({
        filterGroups: initialFilterGroups,
        quickFilters: initialQuickFilters,
        query: ''
      });
    }
  }, [currentView, currentViewFilters.filterGroups, currentViewFilters.quickFilters, hasViews, initialFilter, onSetFilters, onUpdateFilters, uiModeProps.currentUIMode]);
}